/**
 * CategorySelector
 * Searchable multi-select dropdown with selected chips and count label.
 * Matches the legacy Strapi "Add relation" pattern for multi-relations (G3-2).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

type OptionItem = { id: string; name: string; isActive?: boolean };

type CategorySelectorProps = {
  options: OptionItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Heading shown above the field. Defaults to "Categories". */
  label?: string;
  /** Lowercase noun used in the placeholder/empty text. Defaults to "categories". */
  noun?: string;
};

export function CategorySelector({
  options,
  selectedIds,
  onChange,
  label = 'Categories',
  noun = 'categories',
}: CategorySelectorProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOptions = options.filter((o) => selectedIds.includes(o.id));
  const availableOptions = options.filter((o) => !selectedIds.includes(o.id));
  const filtered = search.trim()
    ? availableOptions.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
    : availableOptions;

  const handleAdd = useCallback((id: string) => {
    onChange([...selectedIds, id]);
    setSearch('');
    inputRef.current?.focus();
  }, [selectedIds, onChange]);

  const handleRemove = useCallback((id: string) => {
    onChange(selectedIds.filter((sid) => sid !== id));
  }, [selectedIds, onChange]);

  const count = selectedIds.length;

  return (
    <div ref={containerRef}>
      <span className="block text-sm font-medium text-gray-700">
        {label}{count > 0 && ` (${count})`}
      </span>

      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          className="block w-full rounded-md border border-gray-300 bg-white pl-9 pr-8 py-2 text-sm shadow-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
          placeholder={`Search ${noun}...`}
        />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      </div>

      {isOpen && filtered.length > 0 && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {filtered.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleAdd(opt.id)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 truncate"
              >
                {opt.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isOpen && search.trim() && filtered.length === 0 && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg p-3 text-sm text-gray-500">
            No {noun} found
          </div>
        </div>
      )}

      {selectedOptions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedOptions.map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-sm rounded-md border border-[#026857]/30 bg-[#026857]/5 text-[#026857]"
            >
              {cat.name}
              <button
                type="button"
                onClick={() => handleRemove(cat.id)}
                className="p-0.5 hover:bg-[#026857]/10 rounded"
                aria-label={`Remove ${cat.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
